// Copyright 2026 AllSpice. All rights reserved.

import {ATTRIBUTES_DELIMITER, normalizeFilterPath} from './diff-filter-shared.ts';
import {FilterItem} from './diff-filter-tree.ts';

type AttributeTreeNode = {
  item: FilterItem;
  children: Map<string, AttributeTreeNode>;
};

export function buildAttributeTree(
  attributes: string[],
  attributesToDataIds: Record<string, string[]>,
): FilterItem[] {
  const root = new Map<string, AttributeTreeNode>();

  for (const attr of attributes) {
    const parts = attr.split(ATTRIBUTES_DELIMITER);
    let currentLevel = root;
    let currentPath = '';
    let parentItem: FilterItem | null = null;

    for (const part of parts) {
      currentPath = currentPath ? `${currentPath}${ATTRIBUTES_DELIMITER}${part}` : part;

      if (!currentLevel.has(part)) {
        const componentCount = attributesToDataIds[currentPath]?.length ?? 0;
        const name = `${part} (${componentCount})`;

        currentLevel.set(part, {
          item: new FilterItem(currentPath, name, parentItem),
          children: new Map(),
        });
      }

      parentItem = currentLevel.get(part)!.item;
      currentLevel = currentLevel.get(part)!.children;
    }
  }

  return convertMapToTree(root);
}

function convertMapToTree(map: Map<string, AttributeTreeNode>): FilterItem[] {
  const items: FilterItem[] = [];

  for (const [, value] of map) {
    const item = value.item;
    item.children = convertMapToTree(value.children);
    items.push(item);
  }

  return items.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
}

export function setVisibilityRecursively(items: FilterItem[], visibility: boolean): void {
  for (const item of items) {
    item.isVisible = visibility;
    item.isIndeterminate = false;

    if (item.children.length > 0) {
      setVisibilityRecursively(item.children, visibility);
    }
  }
}

export function collectHiddenFilters(items: FilterItem[], additionalKeys: string[] = []): Set<string> {
  const hiddenFilters = new Set(additionalKeys);

  for (const item of items) {
    if (!item.isVisible) {
      hiddenFilters.add(item.id);
    }

    if (item.children.length > 0) {
      const childHiddenFilters = collectHiddenFilters(item.children);
      for (const filterId of childHiddenFilters) {
        hiddenFilters.add(filterId);
      }
    }
  }

  return hiddenFilters;
}

export function recalculateHiddenDataIds(
  filterList: FilterItem[],
  dataIdsToAttributes: Record<string, string[]>,
): Set<string> {
  const hidden = new Set<string>();
  const hiddenFilters = collectHiddenFilters(filterList);

  for (const [dataId, attributes] of Object.entries(dataIdsToAttributes)) {
    if (attributes.every((attr) => hiddenFilters.has(attr))) {
      hidden.add(dataId);
    }
  }

  return hidden;
}

export function applyStoredFilterOverrides(
  filterList: FilterItem[],
  overrides: Record<string, boolean>,
): void {
  setVisibilityRecursively(filterList, true);
  applyToItems(filterList, overrides);
  updateParentStates(filterList);
}

function applyToItems(
  items: FilterItem[],
  overrides: Record<string, boolean>,
  parentHidden = false,
): void {
  for (const item of items) {
    const normalized = normalizeFilterPath(item.id);
    let itemHidden = parentHidden;

    if (normalized !== '' && normalized in overrides) {
      item.isVisible = overrides[normalized];
      item.isIndeterminate = false;
      itemHidden = !item.isVisible;
    } else if (parentHidden) {
      item.isVisible = false;
      item.isIndeterminate = false;
    }

    if (item.children.length > 0) {
      applyToItems(item.children, overrides, itemHidden);
    }
  }
}

function updateParentStates(items: FilterItem[]): void {
  for (const item of items) {
    if (item.children.length > 0) {
      updateParentStates(item.children);

      const allVisible = item.children.every((child) => child.isVisible && !child.isIndeterminate);
      const anyVisible = item.children.some((child) => child.isVisible || child.isIndeterminate);
      item.isVisible = allVisible;
      item.isIndeterminate = !allVisible && anyVisible;
    }
  }
}

export function buildStoredFilterOverrides(
  filterList: FilterItem[],
  existingOverrides: Record<string, boolean>,
): Record<string, boolean> {
  const overrides = {...existingOverrides};

  updateOverrides(filterList, overrides);

  return overrides;
}

function updateOverrides(
  items: FilterItem[],
  overrides: Record<string, boolean>,
  parentHidden = false,
): void {
  for (const item of items) {
    const normalized = normalizeFilterPath(item.id);
    if (normalized !== '') {
      if (!item.isVisible) {
        overrides[normalized] = false;
      } else if (parentHidden) {
        overrides[normalized] = true;
      } else {
        delete overrides[normalized];
      }
    }

    if (item.children.length > 0) {
      updateOverrides(item.children, overrides, !item.isVisible || parentHidden);
    }
  }
}
